import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import adminApi from "../../lib/adminApi";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Mail, Briefcase, FileText, Bell, BarChart3,
  ChevronDown, Eye, Lock, Search, Download,
} from "lucide-react";

const STATUS_COLOR = {
  sent: "bg-green-50 text-green-700 border border-green-200",
  error: "bg-red-50 text-red-700 border border-red-200",
  mocked: "bg-yellow-50 text-yellow-800 border border-yellow-200",
};

function toCsvValue(v) {
  if (v == null) return "";
  if (typeof v === "object") v = JSON.stringify(v);
  v = String(v);
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function downloadCsv(filename, rows, columns) {
  if (!rows || rows.length === 0) return;
  const header = columns.map((c) => c.label).join(",");
  const body = rows.map((r) => columns.map((c) => {
    const value = typeof c.get === "function" ? c.get(r) : c.key.split(".").reduce((o, k) => (o ? o[k] : ""), r);
    return toCsvValue(value);
  }).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ExportButton({ filename, rows, columns, testid }) {
  const disabled = !rows || rows.length === 0;
  return (
    <button
      onClick={() => downloadCsv(filename, rows, columns)}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-navy border border-slate-200 hover:border-navy disabled:opacity-50 disabled:cursor-not-allowed bg-white"
      data-testid={testid}
    >
      <Download size={14} /> Export CSV
    </button>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon size={16} className="text-navy" />}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-navy">{value}</div>
    </div>
  );
}

function Detail({ open, onClose, row }) {
  if (!open || !row) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={onClose} data-testid="admin-detail-modal">
      <div className="bg-white rounded-[20px] max-w-xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-navy text-lg mb-4">Lead detail</h3>
        <pre className="text-xs bg-soft p-4 rounded-xl overflow-x-auto whitespace-pre-wrap">{JSON.stringify(row, null, 2)}</pre>
        <button onClick={onClose} className="mt-4 px-5 py-2 rounded-full bg-navy text-white font-semibold w-full" data-testid="admin-detail-close">Close</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [hires, setHires] = useState([]);
  const [applications, setApplications] = useState([]);
  const [notifLog, setNotifLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [detailRow, setDetailRow] = useState(null);

  // change password drawer
  const [showPw, setShowPw] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, h, a, n] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/contacts", { params: { type: "general" } }),
        adminApi.get("/admin/contacts", { params: { type: "hire" } }),
        adminApi.get("/admin/applications"),
        adminApi.get("/admin/notification-log"),
      ]);
      setStats(s.data); setContacts(c.data); setHires(h.data); setApplications(a.data); setNotifLog(n.data);
    } catch { /* interceptor handles */ } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setPwBusy(true);
    try {
      await adminApi.post("/admin/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password updated. Please sign in again.");
      setOldPw(""); setNewPw(""); setShowPw(false);
      setTimeout(handleLogout, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally { setPwBusy(false); }
  };

  const filterRows = (rows, fields) => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => fields.some((f) => {
      const v = f.split(".").reduce((o, k) => (o ? o[k] : ""), r);
      return v && String(v).toLowerCase().includes(q);
    }));
  };

  return (
    <div className="min-h-screen bg-soft" data-testid="page-admin">
      <header className="bg-white border-b border-slate-100">
        <div className="container-pad mx-auto max-w-[1400px] flex items-center justify-between h-20">
          <Link to="/admin" className="flex items-center gap-2">
            <img src="/logo.jpg" alt="ENCES" className="w-10 h-10 rounded-xl object-cover" />
            <div className="leading-tight">
              <div className="font-extrabold text-navy">ENCES Admin</div>
              <div className="text-[10px] tracking-[0.25em] text-slate-500 uppercase">Control Room</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-navy border border-slate-200 hover:border-navy" data-testid="admin-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={() => setShowPw(!showPw)} className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-navy border border-slate-200 hover:border-navy" data-testid="admin-change-password-toggle">
              <Lock size={14} /> Password
            </button>
            <div className="hidden md:flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-soft">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-navy text-white text-xs font-bold">
                {(admin?.email || "?")[0].toUpperCase()}
              </span>
              <span className="text-sm font-semibold text-navy truncate max-w-[180px]" data-testid="admin-email">{admin?.email}</span>
            </div>
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-navy text-white hover:bg-red-700" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {showPw && (
        <div className="container-pad mx-auto max-w-[1400px] mt-6">
          <form onSubmit={changePassword} className="bg-white border border-slate-100 rounded-2xl p-5 flex flex-col md:flex-row gap-3 items-end shadow-soft" data-testid="admin-change-password-form">
            <div className="flex-1 w-full">
              <Label htmlFor="old-pw">Current password</Label>
              <Input id="old-pw" type="password" required value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-old-password" />
            </div>
            <div className="flex-1 w-full">
              <Label htmlFor="new-pw">New password (min 8 chars)</Label>
              <Input id="new-pw" type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-new-password" />
            </div>
            <button type="submit" disabled={pwBusy} className="px-5 py-2.5 rounded-full bg-navy text-white font-semibold disabled:opacity-60" data-testid="admin-change-password-submit">{pwBusy ? "Saving..." : "Update password"}</button>
            <button type="button" onClick={() => setShowPw(false)} className="px-5 py-2.5 rounded-full border-2 border-slate-200 text-slate-600 font-semibold">Cancel</button>
          </form>
        </div>
      )}

      <main className="container-pad mx-auto max-w-[1400px] py-8">
        {/* STATS */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats">
          <StatCard label="General leads" value={stats?.general_leads ?? "—"} icon={Mail} testid="stat-general" />
          <StatCard label="Hire requests" value={stats?.hire_leads ?? "—"} icon={Briefcase} testid="stat-hire" />
          <StatCard label="Applications" value={stats?.applications_total ?? "—"} icon={FileText} testid="stat-apps" />
          <StatCard label="Registered users" value={stats?.users_total ?? "—"} icon={BarChart3} testid="stat-users" />
          <StatCard label="Notifications sent" value={stats?.notifications?.sent ?? "—"} icon={Bell} testid="stat-notif" />
        </section>

        {/* SEARCH */}
        <div className="mt-6 bg-white border border-slate-100 rounded-2xl px-4 py-2 flex items-center gap-2 shadow-sm">
          <Search size={16} className="text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter rows in current tab (name, email, message...)"
            className="flex-1 outline-none text-sm text-navy placeholder:text-slate-400"
            data-testid="admin-search"
          />
        </div>

        {/* TABS */}
        <Tabs defaultValue="general" className="mt-6">
          <TabsList className="bg-white border border-slate-100 rounded-full p-1 inline-flex">
            <TabsTrigger value="general" className="rounded-full px-5 data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-general">Contact ({contacts.length})</TabsTrigger>
            <TabsTrigger value="hire" className="rounded-full px-5 data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-hire">Hire ({hires.length})</TabsTrigger>
            <TabsTrigger value="applications" className="rounded-full px-5 data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-applications">Applications ({applications.length})</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-full px-5 data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-notifications">Notification log ({notifLog.length})</TabsTrigger>
          </TabsList>

          {/* General contacts */}
          <TabsContent value="general" className="mt-4">
            <div className="flex justify-end mb-3">
              <ExportButton
                filename={`contacts-general-${new Date().toISOString().slice(0,10)}.csv`}
                rows={filterRows(contacts, ["name", "email", "phone", "message"])}
                columns={[
                  { key: "created_at", label: "Date" },
                  { key: "name", label: "Name" },
                  { key: "email", label: "Email" },
                  { key: "phone", label: "Phone" },
                  { key: "message", label: "Message" },
                  { key: "contact_id", label: "ID" },
                ]}
                testid="export-contacts"
              />
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-soft">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Message</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterRows(contacts, ["name", "email", "phone", "message"]).map((c) => (
                    <TableRow key={c.contact_id} data-testid={`contact-row-${c.contact_id}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-navy">{c.name}</TableCell>
                      <TableCell><a href={`mailto:${c.email}`} className="text-navy hover:underline">{c.email}</a></TableCell>
                      <TableCell>{c.phone || "—"}</TableCell>
                      <TableCell className="max-w-sm truncate text-sm text-slate-600">{c.message}</TableCell>
                      <TableCell><button onClick={() => setDetailRow(c)} className="text-navy hover:text-red-700" data-testid={`view-${c.contact_id}`}><Eye size={16} /></button></TableCell>
                    </TableRow>
                  ))}
                  {!loading && contacts.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">No general leads yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Hire */}
          <TabsContent value="hire" className="mt-4">
            <div className="flex justify-end mb-3">
              <ExportButton
                filename={`hire-requests-${new Date().toISOString().slice(0,10)}.csv`}
                rows={filterRows(hires, ["company_name", "name", "email", "industry", "message"])}
                columns={[
                  { key: "created_at", label: "Date" },
                  { key: "company_name", label: "Company" },
                  { key: "name", label: "Contact" },
                  { key: "email", label: "Email" },
                  { key: "phone", label: "Phone" },
                  { key: "industry", label: "Industry" },
                  { key: "num_positions", label: "# Positions" },
                  { key: "message", label: "Message" },
                  { key: "contact_id", label: "ID" },
                ]}
                testid="export-hire"
              />
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-soft">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Email</TableHead><TableHead>Industry</TableHead><TableHead># Positions</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterRows(hires, ["company_name", "name", "email", "industry", "message"]).map((h) => (
                    <TableRow key={h.contact_id} data-testid={`hire-row-${h.contact_id}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-navy">{h.company_name || "—"}</TableCell>
                      <TableCell>{h.name}</TableCell>
                      <TableCell><a href={`mailto:${h.email}`} className="text-navy hover:underline">{h.email}</a></TableCell>
                      <TableCell>{h.industry || "—"}</TableCell>
                      <TableCell>{h.num_positions || "—"}</TableCell>
                      <TableCell><button onClick={() => setDetailRow(h)} className="text-navy hover:text-red-700"><Eye size={16} /></button></TableCell>
                    </TableRow>
                  ))}
                  {!loading && hires.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No hire requests yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Applications */}
          <TabsContent value="applications" className="mt-4">
            <div className="flex justify-end mb-3">
              <ExportButton
                filename={`applications-${new Date().toISOString().slice(0,10)}.csv`}
                rows={filterRows(applications, ["full_name", "email", "phone", "job.company", "job.title.en"])}
                columns={[
                  { key: "created_at", label: "Date" },
                  { key: "full_name", label: "Applicant" },
                  { key: "email", label: "Email" },
                  { key: "phone", label: "Phone" },
                  { key: "", label: "Job Title", get: (r) => r.job?.title?.en || r.job_id },
                  { key: "", label: "Company", get: (r) => r.job?.company || "" },
                  { key: "", label: "Location", get: (r) => r.job?.location || "" },
                  { key: "status", label: "Status" },
                  { key: "cover_letter", label: "Cover Letter" },
                  { key: "application_id", label: "ID" },
                ]}
                testid="export-applications"
              />
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-soft">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Applicant</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Job</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterRows(applications, ["full_name", "email", "phone", "job.company", "job.title.en"]).map((a) => (
                    <TableRow key={a.application_id} data-testid={`app-row-${a.application_id}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-navy">{a.full_name}</TableCell>
                      <TableCell><a href={`mailto:${a.email}`} className="text-navy hover:underline">{a.email}</a></TableCell>
                      <TableCell>{a.phone || "—"}</TableCell>
                      <TableCell className="text-sm">{a.job?.title?.en || a.job_id} <span className="text-slate-500">· {a.job?.company}</span></TableCell>
                      <TableCell><Badge variant="outline">{a.status}</Badge></TableCell>
                      <TableCell><button onClick={() => setDetailRow(a)} className="text-navy hover:text-red-700"><Eye size={16} /></button></TableCell>
                    </TableRow>
                  ))}
                  {!loading && applications.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No applications yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Notification log */}
          <TabsContent value="notifications" className="mt-4">
            <div className="flex justify-end mb-3">
              <ExportButton
                filename={`notification-log-${new Date().toISOString().slice(0,10)}.csv`}
                rows={filterRows(notifLog, ["to", "subject", "error", "channel"])}
                columns={[
                  { key: "created_at", label: "Date" },
                  { key: "channel", label: "Channel" },
                  { key: "to", label: "To" },
                  { key: "subject", label: "Subject" },
                  { key: "status", label: "Status" },
                  { key: "error", label: "Error" },
                  { key: "notif_id", label: "ID" },
                ]}
                testid="export-notifications"
              />
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-soft">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Channel</TableHead><TableHead>To</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterRows(notifLog, ["to", "subject", "error", "channel"]).map((n) => (
                    <TableRow key={n.notif_id} data-testid={`notif-row-${n.notif_id}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{n.channel}</Badge></TableCell>
                      <TableCell className="text-sm">{n.to}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{n.subject}</TableCell>
                      <TableCell><span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLOR[n.status] || "bg-slate-50 text-slate-600"}`}>{n.status}</span></TableCell>
                      <TableCell className="text-xs text-red-600 max-w-xs truncate" title={n.error}>{n.error || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {!loading && notifLog.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">No notifications yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Detail open={!!detailRow} onClose={() => setDetailRow(null)} row={detailRow} />
    </div>
  );
}
